/**
 * Technical review and final approval — modules C7 and C8, Phase 9.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * THIS IS THE INTEGRITY MECHANISM OF THE PRODUCT, and the shapes below are the
 * whole of it. Six ideas govern them, each an acceptance criterion rather than
 * a preference:
 *
 *  1. REVIEW AND APPROVAL ARE DIFFERENT ACTS. They are separate permissions
 *     (`results.review`, `results.approve`), separate tables and separate
 *     records, even when one person performs both — which is permitted, on
 *     somebody else's work, and recorded twice (D97).
 *
 *  2. NOBODY REVIEWS OR APPROVES THEIR OWN WORK. The performer of an attempt
 *     is refused by the API and again by database trigger (D107). Holding the
 *     permission is not holding the right to use it on oneself.
 *
 *  3. NOTHING ENTERED IS EVER OVERWRITTEN. Every submission takes an immutable
 *     REVISION snapshot, taken by the database itself, so returning a result
 *     and correcting it produces a comparison rather than a replacement, and a
 *     return reason survives as a review action for good (D99).
 *
 *  4. AN APPROVAL LOCKS THE RESULT — in the database, not only in the API. A
 *     lock is never deleted; it is RELEASED, by a recorded correction
 *     procedure that names itself (D101, D102).
 *
 *  5. APPROVING SAYS A RECORD WAS CHECKED. It says NOTHING about the material
 *     (`CLAUDE.md` section 8, D104). There is no conformity field, no pass,
 *     no fail, no limit and no verdict in this file, and there never will be:
 *     that is test science, and it is gated on a validated worked example.
 *
 *  6. AN EMPTY FIELD IS NOT A VALUE. A comparison reports a field that went
 *     away as REMOVED, never as an empty string or a zero.
 */
import { ATTEMPT_STATUS, GENERAL_RESULT_FIELDS } from './execution.js';
import { T96_FRACTION_KEYS } from './coarse-aggregate.js';
import { isD2041Document, isD2726Document, isT85Document, isT96Document, } from './result-templates.js';
// ---------------------------------------------------------------------------
// What a reviewer and an approver may decide
// ---------------------------------------------------------------------------
/**
 * The three technical-review decisions.
 *
 * `technically_accepted` moves the result to the SEPARATE approval stage. It
 * is not an approval and does not lock anything.
 */
export const REVIEW_DECISION = {
    TECHNICALLY_ACCEPTED: 'technically_accepted',
    /** Back to its performer to correct the record, with a mandatory reason. */
    RETURNED_FOR_CORRECTION: 'returned_for_correction',
    /** The work itself must be performed again, with a mandatory reason. */
    RETEST_REQUIRED: 'retest_required',
};
export const ALL_REVIEW_DECISIONS = Object.freeze(Object.values(REVIEW_DECISION));
/** The two approval decisions. */
export const APPROVAL_DECISION = {
    APPROVED: 'approved',
    /** Back to its performer, with a mandatory reason. Review runs again. */
    RETURNED_FOR_CORRECTION: 'returned_for_correction',
};
export const ALL_APPROVAL_DECISIONS = Object.freeze(Object.values(APPROVAL_DECISION));
/**
 * Sending work back, or requiring it to be done again, always says why.
 *
 * Accepting and approving take an OPTIONAL note: the record already says who
 * accepted what and when, and demanding prose for a routine acceptance
 * produces filler rather than information.
 */
export function reviewDecisionRequiresReason(decision) {
    return decision !== REVIEW_DECISION.TECHNICALLY_ACCEPTED;
}
export function approvalDecisionRequiresReason(decision) {
    return decision !== APPROVAL_DECISION.APPROVED;
}
// ---------------------------------------------------------------------------
// THE STATE MACHINE
// ---------------------------------------------------------------------------
/**
 * Every transition an attempt may make, and no others.
 *
 * READ THIS BEFORE CHANGING ANY OF IT. The database enforces the same map
 * independently, and each transition also requires the ACT that justifies it:
 *
 *   submitted → technically_accepted   a review action, by a non-performer
 *   submitted → returned               a review action with a reason
 *   submitted → retest_required        a review action with a reason
 *   technically_accepted → approved    an approval action, by a non-performer
 *   technically_accepted → returned    an approval action with a reason
 *   approved → returned                an OPEN correction request, lock released
 *
 * The two self-transitions of an editable attempt (`draft → draft`,
 * `returned → returned`) are the performer saving their own work and are not
 * listed here; `attemptContentIsEditable` governs those.
 *
 * There is deliberately NO transition from `draft` or `returned` straight to
 * `approved`, none from `submitted` to `approved` (approval never skips
 * review), and none out of `approved`, `retest_required` or `discontinued`
 * except the correction route above.
 */
export const ATTEMPT_TRANSITIONS = Object.freeze({
    [ATTEMPT_STATUS.DRAFT]: Object.freeze([
        ATTEMPT_STATUS.SUBMITTED,
        ATTEMPT_STATUS.DISCONTINUED,
    ]),
    [ATTEMPT_STATUS.SUBMITTED]: Object.freeze([
        ATTEMPT_STATUS.TECHNICALLY_ACCEPTED,
        ATTEMPT_STATUS.RETURNED,
        ATTEMPT_STATUS.RETEST_REQUIRED,
    ]),
    [ATTEMPT_STATUS.RETURNED]: Object.freeze([
        ATTEMPT_STATUS.SUBMITTED,
        ATTEMPT_STATUS.DISCONTINUED,
    ]),
    [ATTEMPT_STATUS.TECHNICALLY_ACCEPTED]: Object.freeze([
        ATTEMPT_STATUS.APPROVED,
        ATTEMPT_STATUS.RETURNED,
    ]),
    /** Only a recorded correction procedure reopens an approved result. */
    [ATTEMPT_STATUS.APPROVED]: Object.freeze([ATTEMPT_STATUS.RETURNED]),
    [ATTEMPT_STATUS.RETEST_REQUIRED]: Object.freeze([]),
    [ATTEMPT_STATUS.DISCONTINUED]: Object.freeze([]),
});
export function attemptTransitionAllowed(from, to) {
    return (ATTEMPT_TRANSITIONS[from] ?? []).includes(to);
}
/** Which decision produces which state. One place, so the two cannot drift. */
export function attemptStatusAfterReview(decision) {
    switch (decision) {
        case REVIEW_DECISION.TECHNICALLY_ACCEPTED:
            return ATTEMPT_STATUS.TECHNICALLY_ACCEPTED;
        case REVIEW_DECISION.RETURNED_FOR_CORRECTION:
            return ATTEMPT_STATUS.RETURNED;
        case REVIEW_DECISION.RETEST_REQUIRED:
            return ATTEMPT_STATUS.RETEST_REQUIRED;
    }
}
export function attemptStatusAfterApproval(decision) {
    return decision === APPROVAL_DECISION.APPROVED
        ? ATTEMPT_STATUS.APPROVED
        : ATTEMPT_STATUS.RETURNED;
}
// ---------------------------------------------------------------------------
// A post-approval correction
// ---------------------------------------------------------------------------
/**
 * A correction is a PROCEDURE, not an edit (D102). Opening one releases the
 * lock on an approved result, records who opened it and why, and sends the
 * result back through review and approval. It resolves only when that work is
 * approved again.
 */
export const CORRECTION_STATUS = {
    OPEN: 'open',
    RESOLVED: 'resolved',
};
export const ALL_CORRECTION_STATUSES = Object.freeze(Object.values(CORRECTION_STATUS));
// ---------------------------------------------------------------------------
// THE COMPLETE LIST OF WHAT A REVIEW RECORD CARRIES
// ---------------------------------------------------------------------------
/**
 * READ CLAUDE.md SECTION 4 AND SECTION 8 BEFORE ADDING AN ENTRY.
 *
 * A review action records a decision, the revision it was taken on, the words
 * the person wrote, and who acted when. NOT ONE ENTRY IS A VERDICT ON THE
 * MATERIAL: there is no conformity, no pass, no fail, no limit and no
 * specification result here. The merge-gate test asserts the API returns
 * nothing beyond these lists.
 */
export const REVIEW_ACTION_FIELDS = Object.freeze([
    'id',
    'decision',
    'revisionNumber',
    'reason',
    'note',
    'actedBy',
    'actedAt',
]);
/** As above, for the separate approval record. */
export const APPROVAL_ACTION_FIELDS = Object.freeze([
    'id',
    'decision',
    'revisionNumber',
    'reason',
    'note',
    'actedBy',
    'actedAt',
]);
/**
 * Words that must never appear in a review or approval payload, because each
 * would assert something about the MATERIAL that this product cannot know
 * until a validated worked example exists (`CLAUDE.md` section 4).
 *
 * Asserted against the real HTTP responses and the rendered pages.
 */
export const FORBIDDEN_CONFORMITY_TERMS = Object.freeze([
    'conformity',
    'conforming',
    'nonconforming',
    'non-conforming',
    'passFail',
    'pass_fail',
    'compliant',
    'complies',
    'acceptanceLimit',
    'specificationMet',
]);
// ---------------------------------------------------------------------------
// What changed between two revisions
// ---------------------------------------------------------------------------
export const REVISION_CHANGE_KIND = {
    UNCHANGED: 'unchanged',
    /** Absent before, present now. */
    ADDED: 'added',
    /** Present before, absent now. An empty field is absent, never zero. */
    REMOVED: 'removed',
    CHANGED: 'changed',
};
function fieldValue(content, field) {
    const value = content[field];
    return typeof value === 'string' && value.length > 0 ? value : null;
}
function changeKind(before, after) {
    if (before === after)
        return REVISION_CHANGE_KIND.UNCHANGED;
    if (before === null)
        return REVISION_CHANGE_KIND.ADDED;
    if (after === null)
        return REVISION_CHANGE_KIND.REMOVED;
    return REVISION_CHANGE_KIND.CHANGED;
}
/**
 * Compare two submissions of the same attempt.
 *
 * WHAT THIS IS FOR: a reviewer looking at a corrected result must be able to
 * see what the technician changed, and a technician must never find their
 * earlier words silently gone. Both revisions are immutable snapshots, so this
 * is a comparison of two facts, not a reconstruction.
 *
 * Every field is reported, including the unchanged ones, so the caller can
 * render a complete side-by-side rather than guessing what was omitted.
 * Equipment and attachments are matched by their own row identity: an entry is
 * added or removed, never "edited", because those rows are immutable too.
 *
 * `before` is `null` for a first submission — then a field that has content is
 * ADDED and an empty one is UNCHANGED, because nothing was there and nothing
 * is there.
 */
/**
 * A structured document as flat `field -> value` pairs, so two revisions
 * compare field by field. Determinations are keyed by their STABLE row id
 * (`d2216.row.<id>.<field>`), so a row added, removed or edited lines up
 * across revisions whatever its position.
 */
export function flattenStructuredDocument(document) {
    const flat = new Map();
    if (!document)
        return flat;
    // ASTM D2726 and D2041 (D260): rows by stable id, as D2216's are.
    if (isD2726Document(document)) {
        flat.set('d2726.methodUsed', document.testInformation.methodUsed);
        for (const row of document.specimens) {
            flat.set(`d2726.row.${row.id}.specimenNumber`, row.specimenNumber);
            flat.set(`d2726.row.${row.id}.dryMassInAir`, row.dryMassInAir);
            flat.set(`d2726.row.${row.id}.ssdMassInAir`, row.ssdMassInAir);
            flat.set(`d2726.row.${row.id}.massInWater`, row.massInWater);
            flat.set(`d2726.row.${row.id}.bulkSpecificGravity`, row.calculation?.bulkSpecificGravity ?? null);
        }
        flat.set('d2726.averageBulkSpecificGravity', document.summary.averageBulkSpecificGravity);
        flat.set('d2726.ambientTemperature', document.environment.ambientTemperature);
        flat.set('d2726.relativeHumidity', document.environment.relativeHumidity);
        flat.set('d2726.remarks', document.remarks);
        return flat;
    }
    if (isD2041Document(document)) {
        flat.set('d2041.containerType', document.testInformation.containerType);
        flat.set('d2041.procedure', document.testInformation.procedure);
        for (const row of document.determinations) {
            flat.set(`d2041.row.${row.id}.specimenNumber`, row.specimenNumber);
            flat.set(`d2041.row.${row.id}.dryMassInAir`, row.dryMassInAir);
            flat.set(`d2041.row.${row.id}.containerWaterMass`, row.containerWaterMass);
            flat.set(`d2041.row.${row.id}.containerSampleWaterMass`, row.containerSampleWaterMass);
            flat.set(`d2041.row.${row.id}.maximumSpecificGravity`, row.calculation?.maximumSpecificGravity ?? null);
        }
        flat.set('d2041.averageMaximumSpecificGravity', document.summary.averageMaximumSpecificGravity);
        flat.set('d2041.ambientTemperature', document.environment.ambientTemperature);
        flat.set('d2041.relativeHumidity', document.environment.relativeHumidity);
        flat.set('d2041.remarks', document.remarks);
        return flat;
    }
    // AASHTO T85 and T96 (D267): T85 rows by stable id; T96 fractions by key.
    if (isT85Document(document)) {
        for (const row of document.determinations) {
            flat.set(`t85.row.${row.id}.dryMassInAir`, row.dryMassInAir);
            flat.set(`t85.row.${row.id}.ssdMassInAir`, row.ssdMassInAir);
            flat.set(`t85.row.${row.id}.massInWater`, row.massInWater);
            flat.set(`t85.row.${row.id}.absorptionPercent`, row.calculation?.absorptionPercent ?? null);
            flat.set(`t85.row.${row.id}.bulkSpecificGravitySsd`, row.calculation?.bulkSpecificGravitySsd ?? null);
            flat.set(`t85.row.${row.id}.bulkSpecificGravityOd`, row.calculation?.bulkSpecificGravityOd ?? null);
            flat.set(`t85.row.${row.id}.apparentSpecificGravity`, row.calculation?.apparentSpecificGravity ?? null);
        }
        for (const [key, value] of Object.entries(document.summary))
            flat.set(`t85.${key}`, value);
        flat.set('t85.remarks', document.remarks);
        return flat;
    }
    if (isT96Document(document)) {
        for (const [key, value] of Object.entries(document.testInformation))
            flat.set(`t96.${key}`, value);
        for (const key of T96_FRACTION_KEYS)
            flat.set(`t96.grading.${key}`, document.grading[key]);
        flat.set('t96.retainedMassNo12', document.retainedMassNo12);
        flat.set('t96.totalInitialMass', document.calculation?.totalInitialMass ?? null);
        flat.set('t96.passingMassNo12', document.calculation?.passingMassNo12 ?? null);
        flat.set('t96.lossPercent', document.calculation?.lossPercent ?? null);
        flat.set('t96.remarks', document.remarks);
        return flat;
    }
    const info = document.testInformation;
    flat.set('d2216.methodUsed', info.methodUsed);
    flat.set('d2216.ovenTemperature', info.ovenTemperature);
    flat.set('d2216.ovenStartedAt', info.ovenStartedAt);
    flat.set('d2216.nominalMaximumSize', info.nominalMaximumSize);
    for (const row of document.determinations) {
        flat.set(`d2216.row.${row.id}.containerNumber`, row.containerNumber);
        flat.set(`d2216.row.${row.id}.containerMass`, row.containerMass);
        flat.set(`d2216.row.${row.id}.containerWetMass`, row.containerWetMass);
        flat.set(`d2216.row.${row.id}.containerDryMass`, row.containerDryMass);
        flat.set(`d2216.row.${row.id}.waterContentPercent`, row.calculation?.waterContentPercent ?? null);
    }
    flat.set('d2216.ambientTemperature', document.environment.ambientTemperature);
    flat.set('d2216.relativeHumidity', document.environment.relativeHumidity);
    flat.set('d2216.remarks', document.remarks);
    return flat;
}
export function compareRevisions(before, after) {
    const isStructured = Boolean(after.structured);
    // A structured revision holds no general summary: comparing those empty
    // fields would only list "unchanged" noise above the real changes.
    const generalFields = isStructured
        ? []
        : GENERAL_RESULT_FIELDS.map((field) => {
            const previous = before ? fieldValue(before.content, field) : null;
            const current = fieldValue(after.content, field);
            return {
                field,
                kind: changeKind(previous, current),
                before: previous,
                after: current,
            };
        });
    // Structured fields are listed only when something changed and there is a
    // previous revision: the first submission is shown in full elsewhere.
    const structuredFields = [];
    if (isStructured && before) {
        const previous = flattenStructuredDocument(before.structured);
        const current = flattenStructuredDocument(after.structured);
        const keys = [...new Set([...previous.keys(), ...current.keys()])];
        for (const field of keys) {
            const was = previous.get(field) ?? null;
            const now = current.get(field) ?? null;
            const kind = changeKind(was, now);
            if (kind !== REVISION_CHANGE_KIND.UNCHANGED) {
                structuredFields.push({ field, kind, before: was, after: now });
            }
        }
        for (const field of ['executionStartedAt', 'executionCompletedAt']) {
            const was = fieldValue(before.content, field);
            const now = fieldValue(after.content, field);
            const kind = changeKind(was, now);
            if (kind !== REVISION_CHANGE_KIND.UNCHANGED) {
                structuredFields.push({ field, kind, before: was, after: now });
            }
        }
    }
    const fields = [...generalFields, ...structuredFields];
    const previousUsage = new Set((before?.equipment ?? []).map((row) => row.usageId));
    const currentUsage = new Set(after.equipment.map((row) => row.usageId));
    const equipment = [
        ...after.equipment
            .filter((row) => !previousUsage.has(row.usageId))
            .map((entry) => ({ kind: REVISION_CHANGE_KIND.ADDED, entry })),
        ...(before?.equipment ?? [])
            .filter((row) => !currentUsage.has(row.usageId))
            .map((entry) => ({ kind: REVISION_CHANGE_KIND.REMOVED, entry })),
    ];
    const previousFiles = new Set((before?.attachments ?? []).map((row) => row.attachmentId));
    const currentFiles = new Set(after.attachments.map((row) => row.attachmentId));
    const attachments = [
        ...after.attachments
            .filter((row) => !previousFiles.has(row.attachmentId))
            .map((entry) => ({ kind: REVISION_CHANGE_KIND.ADDED, entry })),
        ...(before?.attachments ?? [])
            .filter((row) => !currentFiles.has(row.attachmentId))
            .map((entry) => ({ kind: REVISION_CHANGE_KIND.REMOVED, entry })),
    ];
    return {
        fromRevision: before?.revisionNumber ?? null,
        toRevision: after.revisionNumber,
        fields,
        equipment,
        attachments,
        hasChanges: fields.some((field) => field.kind !== REVISION_CHANGE_KIND.UNCHANGED) ||
            equipment.length > 0 ||
            attachments.length > 0,
    };
}
//# sourceMappingURL=review.js.map